import { useCallback, useEffect, useMemo, useState, useContext } from "react";
import useUser, { getAuthorized, isUserClient, postAuthorized } from "../../../hooks/User";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import config from "../../../Config";
import { precacheSurveyMediaFromPayload } from "../../../utils/precacheSurveyMedia";
import {
    enqueueSurveyOutbox,
    getSurveySnapshot,
    putSurveySnapshot,
} from "../../../utils/surveyOfflineDb";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import SEO from "../../../components/SEO";
import { formatTimeInUserTimezone } from "../../../utils/timezone";

import "./surveypassing.css";

const TAKE_SURVEY_STORAGE_KEY = "spiria:takeSurvey";

function parsePositiveInt(value) {
    if (value === undefined || value === null || value === "") return null;
    const n = parseInt(String(value), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function isNetworkError(error) {
    if (!error || error.response) return false;
    const c = error.code;
    return (
        c === "ERR_NETWORK" ||
        c === "ECONNABORTED" ||
        (error.message && String(error.message).includes("Network Error"))
    );
}

// Icons
const ArrowLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m15 18-6-6 6-6"/>
    </svg>
);

const ArrowRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m9 18 6-6-6-6"/>
    </svg>
);

const SendIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
);

const ImageIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
        <circle cx="9" cy="9" r="2"/>
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
    </svg>
);

const MicIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 19c0 1.7-1.3 3-3 3s-3-1.3-3-3"/>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
        <line x1="12" x2="12" y1="19" y2="22"/>
        <rect x="6" y="2" width="12" height="12" rx="6"/>
    </svg>
);

const FileTextIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
        <polyline points="14 2 14 8 20 8"/>
    </svg>
);

const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
);

// Question Type Enum
const QuestionTypeEnum = Object.freeze({
    text: "text",
    image: "picture",
    voice: "voice",
    mixed: "combined"
});

// Question Components
function TextQuestion({ question }) {
    return (
        <div className="question-text-content">
            <FileTextIcon />
            <h3>{question.question_text}</h3>
        </div>
    );
}

function ImageQuestion({ question }) {
    return (
        <div className="question-media">
            <img 
                className="question-image" 
                src={config.getMediaUrl(question?.picture_filename)} 
                alt="Изображение к вопросу"
                onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                }}
            />
            <div className="image-error" style={{display: 'none'}}>
                <ImageIcon />
                <span>Изображение недоступно</span>
            </div>
        </div>
    );
}

function VoiceQuestion({ question }) {
    return (
        <div className="question-media">
            <audio controls className="question-audio">
                <source src={config.getMediaUrl(question?.voice_filename)} type="audio/mpeg"/>
                Ваш браузер не поддерживает аудио
            </audio>
        </div>
    );
}

function MixedQuestion({ question }) {
    return (
        <div className="question-mixed">
            <ImageQuestion question={question}/>
            <VoiceQuestion question={question}/>
        </div>
    );
}

// Option Component
function Option({ value, name, isSelected, onSelect }) {
    return (
        <div 
            className={`answer-option ${isSelected ? 'selected' : ''}`}
            onClick={() => onSelect(value)}
        >
            <div className="option-radio">
                {isSelected && <div className="option-radio-inner" />}
            </div>
            <span className="option-label">{value}</span>
        </div>
    );
}

// Progress Bar
function ProgressBar({ current, total }) {
    const progress = ((current + 1) / total) * 100;
    
    return (
        <div className="progress-container">
            <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-text">{current + 1} / {total}</span>
        </div>
    );
}

// Main Survey Passing Component
function SurveyPassing() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { setErrorList, addNotification } = useContext(ErrorHandlerContext);

    const { surveyId, reminderId } = useMemo(() => {
        const fromState = location?.state || {};
        let sid =
            parsePositiveInt(fromState.surveyId) ??
            parsePositiveInt(searchParams.get("surveyId"));
        let rid =
            fromState.reminderId != null && fromState.reminderId !== ""
                ? parsePositiveInt(fromState.reminderId)
                : parsePositiveInt(searchParams.get("reminderId"));
        try {
            const raw = sessionStorage.getItem(TAKE_SURVEY_STORAGE_KEY);
            if (raw) {
                const p = JSON.parse(raw);
                if (sid == null && p.surveyId != null) sid = parsePositiveInt(p.surveyId);
                if (rid == null && p.reminderId != null) rid = parsePositiveInt(p.reminderId);
            }
        } catch {
            /* ignore */
        }
        return { surveyId: sid, reminderId: rid };
    }, [location.state, searchParams]);

    useEffect(() => {
        if (surveyId) {
            try {
                sessionStorage.setItem(
                    TAKE_SURVEY_STORAGE_KEY,
                    JSON.stringify({ surveyId, reminderId: reminderId ?? null })
                );
            } catch {
                /* ignore */
            }
        }
    }, [surveyId, reminderId]);

    useEffect(() => {
        const onFlushed = (e) => {
            const n = e.detail?.count ?? 0;
            if (n > 0) {
                addNotification({
                    msg: n === 1 ? "Опрос отправлен на сервер." : `Отправлено ответов: ${n}.`,
                    type: "success",
                    duration: 6000,
                });
            }
        };
        window.addEventListener("spiria-survey-outbox-flushed", onFlushed);
        return () => window.removeEventListener("spiria-survey-outbox-flushed", onFlushed);
    }, [addNotification]);

    const [surveyData, setSurveyData] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [savedOffline, setSavedOffline] = useState(false);
    const [completionMessage, setCompletionMessage] = useState("");
    const [doctorName, setDoctorName] = useState("");
    const [nextSurveyTime, setNextSurveyTime] = useState("");
    const [loadingCompletion, setLoadingCompletion] = useState(false);

    useEffect(() => {
        if (!surveyId) {
            navigate(ROUTES.MY_SURVEYS, { viewTransition: true });
            return;
        }

        const applySurveyData = (data) => {
            setSurveyData(data);
            setQuestions(data.questions || []);
            const initialAnswers = (data.questions || []).map((q) => ({
                question_in_survey_id: q.question_in_survey_id,
                text: null,
            }));
            setAnswers(initialAnswers);
        };

        const loadSurvey = async () => {
            try {
                const online = typeof navigator !== "undefined" && navigator.onLine;
                let data = null;

                if (online) {
                    try {
                        const response = await getAuthorized("/client/get_survey", {
                            params: { survey_id: surveyId },
                        });
                        data = response.data;
                        await putSurveySnapshot(surveyId, data);
                        await precacheSurveyMediaFromPayload(data);
                    } catch (error) {
                        if (isNetworkError(error)) {
                            const snap = await getSurveySnapshot(surveyId);
                            if (snap?.data) data = snap.data;
                        }
                        if (!data) throw error;
                    }
                } else {
                    const snap = await getSurveySnapshot(surveyId);
                    if (!snap?.data) {
                        setErrorList((prev) => [
                            ...prev,
                            "Нет сети и нет сохранённой копии опроса. Откройте «Назначенные опросы» онлайн для загрузки.",
                        ]);
                        navigate(ROUTES.MY_SURVEYS, { viewTransition: true });
                        return;
                    }
                    data = snap.data;
                }

                applySurveyData(data);
            } catch (error) {
                console.error("Failed to load survey:", error);
                setErrorList((prev) => [
                    ...prev,
                    error.response?.data?.error_description || "Ошибка при загрузке опроса",
                ]);
                navigate(ROUTES.MY_SURVEYS, { viewTransition: true });
            } finally {
                setLoading(false);
            }
        };

        loadSurvey();
    }, [surveyId, navigate, setErrorList]);

    const question = questions[currentQuestion];

    const handleAnswer = useCallback((text) => {
        setAnswers(prev => {
            const newAnswers = [...prev];
            newAnswers[currentQuestion] = {
                ...newAnswers[currentQuestion],
                text
            };
            return newAnswers;
        });
    }, [currentQuestion]);

    const handleNext = useCallback(() => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(prev => prev + 1);
        }
    }, [currentQuestion, questions.length]);

    const handlePrev = useCallback(() => {
        if (currentQuestion > 0) {
            setCurrentQuestion(prev => prev - 1);
        }
    }, [currentQuestion]);

    const handleSubmit = useCallback(async () => {
        setSubmitting(true);
        setSavedOffline(false);
        const clientSubmissionId =
            typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const body = {
            survey_id: surveyId,
            reminder_id: reminderId ?? null,
            answers,
            client_submission_id: clientSubmissionId,
        };
        try {
            const response = await postAuthorized("/client/survey_attempt", body);
            setCompletionMessage(response?.data?.message || "");
            setCompleted(true);
            setLoadingCompletion(true);

            // Fetch doctor name and next survey time in parallel
            const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");
            try {
                const [doctorsRes, remindersRes] = await Promise.all([
                    getAuthorized("/client/my_doctors").catch(() => null),
                    getAuthorized("/client/my_reminders", {
                        params: { for_date: tomorrow, status: "sent" }
                    }).catch(() => null),
                ]);

                if (doctorsRes?.data?.length > 0) {
                    setDoctorName(doctorsRes.data[0].full_name);
                }
                if (remindersRes?.data?.reminders?.length > 0) {
                    const nextTime = formatTimeInUserTimezone(
                        remindersRes.data.reminders[0].scheduled_time,
                        "HH:mm"
                    );
                    setNextSurveyTime(nextTime);
                }
            } catch {
                // Non-critical — just skip the extra info
            } finally {
                setLoadingCompletion(false);
            }
        } catch (error) {
            if (isNetworkError(error) || (typeof navigator !== "undefined" && !navigator.onLine)) {
                try {
                    await enqueueSurveyOutbox({ clientSubmissionId, body });
                    setSavedOffline(true);
                    setCompleted(true);
                    setTimeout(() => {
                        navigate(ROUTES.MY_SURVEYS, { viewTransition: true });
                    }, 3500);
                } catch (e) {
                    console.error("Failed to queue survey:", e);
                    setErrorList((prev) => [
                        ...prev,
                        "Не удалось сохранить ответ локально. Попробуйте снова.",
                    ]);
                }
            } else {
                console.error("Failed to submit survey:", error);
                setErrorList((prev) => [
                    ...prev,
                    error.response?.data?.error_description || "Ошибка при отправке опроса",
                ]);
            }
        } finally {
            setSubmitting(false);
        }
    }, [surveyId, reminderId, answers, navigate, setErrorList]);

    const handleBack = useCallback(() => {
        navigate(ROUTES.MY_SURVEYS, { viewTransition: true });
    }, [navigate]);

    if (loading) {
        return (
            <div className="survey-passing-page">
                <div className="survey-loading">
                    <div className="spinner"></div>
                    <p>Загрузка опроса...</p>
                </div>
            </div>
        );
    }

    if (completed) {
        return (
            <div className="survey-passing-page">
                <div className="survey-completed">
                    <div className="completed-card">
                        {/* Success Icon */}
                        <div className="completed-icon-wrapper">
                            <div className="completed-icon">
                                <CheckCircleIcon />
                            </div>
                        </div>

                        <h2 className="completed-title">Опрос завершён!</h2>

                        {savedOffline ? (
                            <div className="completed-offline">
                                <p className="completed-thanks">Спасибо за прохождение опроса!</p>
                                <div className="completed-info-row offline">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                    <span>Ответ сохранён на устройстве.</span>
                                </div>
                                <p className="completed-hint">
                                    Когда появится интернет, зайдите в Spiria ещё раз — ответ уйдёт на сервер автоматически.
                                </p>
                            </div>
                        ) : (
                            <div className="completed-online">
                                <p className="completed-thanks">
                                    {completionMessage || "Спасибо за прохождение опроса!"}
                                </p>

                                {loadingCompletion ? (
                                    <div className="completed-loading">
                                        <div className="spinner-small"></div>
                                    </div>
                                ) : (
                                    <div className="completed-details">
                                        {doctorName && (
                                            <div className="completed-info-row doctor">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                                <span>Ваш ответ направлен врачу <strong>{doctorName}</strong></span>
                                            </div>
                                        )}

                                        {nextSurveyTime && (
                                            <div className="completed-info-row next-survey">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                <span>Не забудьте пройти опрос завтра в <strong>{nextSurveyTime}</strong></span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <button
                            className="completed-back-btn"
                            onClick={handleBack}
                        >
                            Вернуться к опросам
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!question) {
        return null;
    }

    const currentAnswer = answers[currentQuestion]?.text;
    const isFirst = currentQuestion === 0;
    const isLast = currentQuestion === questions.length - 1;
    const canProceed = currentAnswer !== null && currentAnswer !== "";

    return (
        <div className="survey-passing-page">
            <div className="survey-passing-container">
                {/* Header */}
                <div className="survey-header">
                    <button className="btn-back" onClick={handleBack}>
                        <ArrowLeftIcon />
                        <span>Назад</span>
                    </button>
                    <div className="survey-info">
                        <h1>{surveyData?.title}</h1>
                        {surveyData?.description && (
                            <p>{surveyData.description}</p>
                        )}
                    </div>
                </div>

                {/* Progress */}
                <ProgressBar current={currentQuestion} total={questions.length} />

                {/* Question Card */}
                <div className="question-card">
                    {/* Question Content */}
                    <div className="question-header">
                        <TextQuestion question={question} />
                    </div>

                    {/* Media Content */}
                    <div className="question-media-content">
                        {(() => {
                            switch(question?.question_type) {
                                case QuestionTypeEnum.image:
                                    return <ImageQuestion question={question}/>;
                                case QuestionTypeEnum.voice:
                                    return <VoiceQuestion question={question}/>;
                                case QuestionTypeEnum.mixed:
                                    return <MixedQuestion question={question}/>;
                                default:
                                    return null;
                            }
                        })()}
                    </div>

                    {/* Answer Section */}
                    <div className="answer-section">
                        {question?.answer_options && question.answer_options.length > 0 ? (
                            <div className="answer-options">
                                {question.answer_options.map((option, index) => (
                                    <Option 
                                        key={index}
                                        value={option}
                                        name={`question-${currentQuestion}`}
                                        isSelected={currentAnswer === option}
                                        onSelect={handleAnswer}
                                    />
                                ))}
                            </div>
                        ) : (
                            <textarea 
                                className="answer-textarea"
                                placeholder="Введите ваш ответ..."
                                value={currentAnswer || ""}
                                onChange={(e) => handleAnswer(e.target.value)}
                                rows={5}
                            />
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <div className="survey-navigation">
                    <button 
                        className="btn-nav btn-prev"
                        onClick={handlePrev}
                        disabled={isFirst}
                    >
                        <ArrowLeftIcon />
                        <span>Назад</span>
                    </button>
                    
                    {isLast ? (
                        <button 
                            className="btn-nav btn-submit"
                            onClick={handleSubmit}
                            disabled={!canProceed || submitting}
                        >
                            {submitting ? (
                                <span className="loading-dots">Отправка...</span>
                            ) : (
                                <>
                                    <SendIcon />
                                    <span>Отправить</span>
                                </>
                            )}
                        </button>
                    ) : (
                        <button 
                            className="btn-nav btn-next"
                            onClick={handleNext}
                            disabled={!canProceed}
                        >
                            <span>Далее</span>
                            <ArrowRightIcon />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// Page Component with Error Handler
export default function SurveyPassingPage() {
    const { loading, error, data } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        // Разрешаем доступ всем пользователям с ролью client (включая врачей)
        if (data && !isUserClient(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [loading, error, data, navigate]);

    if (loading) {
        return (
            <div className="survey-passing-page">
                <div className="survey-loading">
                    <div className="spinner"></div>
                    <p>Загрузка...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <SEO 
                title="Прохождение опроса" 
                description="Ответьте на вопросы медицинского опросника для оценки состояния здоровья" 
                noindex={true} 
            />
            <ErrorHandlerContextProvider>
                <SurveyPassing />
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}
